import { Link } from "react-router-dom";
import {
  ArrowRight,
  Briefcase,
  SearchCheck,
  Scale,
  Heart,
  Settings,
  Shield,
  CheckCircle,
  MapPin,
  Target,
  Eye,
  Users,
  Lock,
  Award,
  Clock,
  Monitor,
  FileText,
  AlertTriangle,
  Building2,
  Gavel,
  ShieldCheck,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { services, images } from "../data/mock";
import { useSiteConfig } from "../contexts/SiteConfigContext";
import { usePageSEO } from "../hooks/usePageSEO";

const iconMap = {
  Briefcase,
  SearchCheck,
  Scale,
  Heart,
  Settings,
  MapPin,
  Target,
  Shield,
  Eye,
  Users,
  Lock,
  Award,
  Clock,
  Monitor,
  FileText,
  AlertTriangle,
  Building2,
  Gavel,
  ShieldCheck,
};

export default function ServicesPage() {
  const { services: svcData, images } = useSiteConfig();
  const services = svcData || [];
  
  usePageSEO({
    title: "Our Services",
    description: "Comprehensive polygraph services: Employment & HR screening, corporate investigations, legal support, private matters, and onsite/offsite testing across Africa.",
    path: "/services"
  });
  
  return (
    <main>
      {/* Hero Banner */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-[#0A0A0F]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#DC2626]/[0.03] to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-[#DC2626] font-semibold tracking-[0.3em] text-xs mb-4 uppercase">
            Our Services
          </p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Polygraph Services
            <br />
            <span className="text-[#DC2626]">You Can Trust</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl leading-relaxed">
            From corporate screening to personal matters, we offer comprehensive
            polygraph examination services tailored to your specific needs.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="bg-[#0A0A0F] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-8">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon] || Shield;
              const isEven = index % 2 === 0;
              return (
                <div
                  key={service.id}
                  className="group bg-[#111118] border border-white/[0.04] rounded-2xl overflow-hidden hover:border-[#DC2626]/20 transition-all duration-500"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                    {/* Number + Icon */}
                    <div className="lg:col-span-2 p-8 lg:p-10 flex lg:flex-col items-center lg:items-start gap-4 lg:gap-6 border-b lg:border-b-0 lg:border-r border-white/[0.04]">
                      <span className="text-[#DC2626]/30 font-display text-4xl lg:text-5xl font-bold">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <div className="w-12 h-12 rounded-xl bg-[#DC2626]/[0.08] flex items-center justify-center group-hover:bg-[#DC2626]/[0.15] transition-colors duration-300">
                        <Icon className="h-5 w-5 text-[#DC2626]" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="lg:col-span-10 p-8 lg:p-10">
                      <h3 className="text-white font-semibold text-2xl mb-3">
                        {service.title}
                      </h3>
                      <p className="text-gray-400 leading-relaxed mb-6 max-w-2xl">
                        {service.shortDescription}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                        {service.features.map((feature, fi) => (
                          <div key={fi} className="flex items-center gap-3">
                            <CheckCircle className="h-4 w-4 text-[#DC2626] shrink-0" />
                            <span className="text-gray-300 text-sm">
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>
                      <Link to="/contact">
                        <Button className="bg-[#DC2626]/10 hover:bg-[#DC2626] text-[#DC2626] hover:text-white border border-[#DC2626]/30 hover:border-[#DC2626] font-medium px-6 transition-all duration-300">
                          Request This Service
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 lg:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={images.detective}
            alt=""
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-[#0A0A0F]/93" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
            Not Sure Which Service You Need?
          </h2>
          <p className="text-gray-400 text-lg mb-10 leading-relaxed">
            Contact us for a free, confidential consultation. We'll help
            determine the best approach for your specific situation.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-[#DC2626] hover:bg-[#B91C1C] text-white px-10 h-13 text-base font-medium transition-colors duration-200"
            >
              Get a Free Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
}
